import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useQuery } from "react-query";
import Loader from "../../components/Loader";
import { getVariationTickets } from "./mutation";

const STATUSES = ["PENDING", "REJECTED", "RESOLVED"];
const STATUS_COLORS = { PENDING: "warning", REJECTED: "error", RESOLVED: "success" };
const HISTORY_DAYS = 7;

// YYYY-MM-DD in local time, days back from today
const localDate = (daysBack = 0) => {
  const date = new Date();
  date.setDate(date.getDate() - daysBack);
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 10);
};

const formatDateTime = (value) =>
  value ? new Date(value).toLocaleString("en-IN") : "-";

const solutionState = (solution) => {
  if (solution?.accepted === true) return "Accepted";
  if (solution?.accepted === false) return "Declined";
  return solution?.solution ? "Awaiting approval" : "No solution yet";
};

function TicketHistory() {
  const [from, setFrom] = useState(localDate(HISTORY_DAYS));
  const [to, setTo] = useState(localDate());
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [selected, setSelected] = useState(null);

  const params = { from, to, status: status || undefined, page, pageSize };
  const { data, isLoading } = useQuery(
    ["production-variation-tickets", params],
    () => getVariationTickets(params),
    { keepPreviousData: true }
  );
  const rows = data?.rows ?? [];

  const setFilter = (setter) => (event) => {
    setter(event.target.value);
    setPage(0);
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center", mb: 2 }}>
        <TextField
          size="small"
          type="date"
          label="Shift date from"
          InputLabelProps={{ shrink: true }}
          value={from}
          onChange={setFilter(setFrom)}
        />
        <TextField
          size="small"
          type="date"
          label="to"
          InputLabelProps={{ shrink: true }}
          value={to}
          onChange={setFilter(setTo)}
        />
        <TextField
          select
          size="small"
          label="Status"
          sx={{ minWidth: 150 }}
          value={status}
          onChange={setFilter(setStatus)}
        >
          <MenuItem value="">All</MenuItem>
          {STATUSES.map((value) => (
            <MenuItem key={value} value={value}>
              {value}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {isLoading ? (
        <Loader />
      ) : (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Ticket #</TableCell>
                  <TableCell>Shift date</TableCell>
                  <TableCell>Shift</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Machine</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Raised at</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No automatic tickets for these filters
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((ticket) => (
                  <TableRow key={ticket.id} hover>
                    <TableCell>{ticket.slNo}</TableCell>
                    <TableCell>{ticket.shiftDate}</TableCell>
                    <TableCell>{ticket.shift}</TableCell>
                    <TableCell>{ticket.department || "-"}</TableCell>
                    <TableCell>{ticket.machine}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={ticket.status}
                        color={STATUS_COLORS[ticket.status] ?? "default"}
                      />
                    </TableCell>
                    <TableCell>{formatDateTime(ticket.createdAt)}</TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => setSelected(ticket)}>
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={data?.total ?? 0}
            page={page}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[25, 50, 100]}
            onPageChange={(_, next) => setPage(next)}
            onRowsPerPageChange={(event) => {
              setPageSize(Number(event.target.value));
              setPage(0);
            }}
          />
        </>
      )}

      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Ticket #{selected?.slNo}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 2 }}>
            <Chip
              size="small"
              label={selected?.status}
              color={STATUS_COLORS[selected?.status] ?? "default"}
            />
            <Typography variant="body2" color="text.secondary">
              Raised by {selected?.reportedBy ?? "-"} on{" "}
              {formatDateTime(selected?.createdAt)}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mb: 2 }}>
            {selected?.description}
          </Typography>
          <Typography variant="subtitle2" gutterBottom>
            Managers
          </Typography>
          {(selected?.reportingManagerSolution ?? selected?.reportingManager ?? []).map(
            (manager) => (
              <Box key={manager.id} sx={{ mb: 1 }}>
                <Typography variant="body2" fontWeight={600}>
                  {manager.name} · {solutionState(manager)}
                </Typography>
                {manager.solution && (
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {manager.solution}
                  </Typography>
                )}
              </Box>
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export default TicketHistory;
